/**
 * Comparing a carried item against the one already worn.
 *
 * Pure and tested. Everything here answers one of two questions a player asks of
 * every drop — *can I wear this?* and *is it better?* — and the second is the one
 * a tooltip full of raw affixes does not answer at all.
 */

import type { Attributes, InventoryItem, ItemAffix, StatRequirements } from '../net/protocol';

/** One attribute a requirement asks for and the player does not have. */
export interface Shortfall {
  attribute: 'STR' | 'DEX' | 'INT' | 'END';
  need: number;
  have: number;
}

const GATES = [
  { key: 'str', label: 'STR' },
  { key: 'dex', label: 'DEX' },
  { key: 'int', label: 'INT' },
  { key: 'end', label: 'END' },
] as const;

/**
 * Which of an item's requirements the player fails, and by how much.
 *
 * Empty means wearable. Returns empty when attributes are unknown too — before
 * the first `CHARACTER` frame there is nothing to compare against, and claiming a
 * shortfall would be inventing one.
 */
export function unmetFor(
  requires: StatRequirements | undefined,
  total: Attributes | null,
): Shortfall[] {
  if (!requires || !total) return [];
  const out: Shortfall[] = [];
  for (const { key, label } of GATES) {
    const need = requires[key];
    if (need > 0 && total[key] < need) out.push({ attribute: label, need, have: total[key] });
  }
  return out;
}

/** "20 STR · 15 DEX", or "" when an item asks for nothing. */
export function requirementText(requires: StatRequirements | undefined): string {
  if (!requires) return '';
  return GATES.filter(({ key }) => requires[key] > 0)
    .map(({ key, label }) => `${requires[key]} ${label}`)
    .join(' · ');
}

/**
 * Why an item cannot be worn, as something the player can act on.
 *
 * "Requires 20 STR" is a fact; "20 STR — you have 15" is a plan. The same wording
 * choice the skills panel makes, for the same reason.
 */
export function shortfallText(unmet: readonly Shortfall[]): string {
  return unmet.map((s) => `${s.need} ${s.attribute} — you have ${s.have}`).join(', ');
}

/** One line of a comparison: a stat, both values, and which way it moves. */
export interface Delta {
  label: string;
  from: number;
  to: number;
  /** +1 better, -1 worse, 0 unchanged. */
  direction: -1 | 0 | 1;
}

/** The arrow for a direction. Empty for no change — a row of `=` is noise. */
export function arrow(direction: -1 | 0 | 1): string {
  if (direction > 0) return '▲';
  if (direction < 0) return '▼';
  return '';
}

/**
 * Sums an item's affixes by stat key, so two items can be compared line by line.
 *
 * Affixes are a list, not a map, and an item may roll the same stat twice — a
 * prefix and a suffix both granting armour. Summing is the only reading that makes
 * a comparison honest.
 */
export function statTotals(item: InventoryItem | undefined): Map<string, number> {
  const out = new Map<string, number>();
  if (!item) return out;
  if (item.armour) out.set('armour', item.armour);
  for (const affix of item.affixes) {
    out.set(affix.stat, (out.get(affix.stat) ?? 0) + affix.value);
  }
  return out;
}

/**
 * The differences between a candidate and what is worn.
 *
 * Every stat either item has, so a stat the new item *lacks* shows as a loss
 * rather than vanishing — the most important comparison a player makes is what
 * they are giving up, and a tooltip that only lists gains hides it.
 *
 * Sorted with the changes first: a list where the meaningful rows are scattered
 * among unchanged ones is a list nobody reads.
 */
export function compareItems(
  candidate: InventoryItem | undefined,
  worn: InventoryItem | undefined,
): Delta[] {
  const a = statTotals(candidate);
  const b = statTotals(worn);
  const keys = [...new Set([...a.keys(), ...b.keys()])].sort();

  const deltas = keys.map((key) => {
    const to = a.get(key) ?? 0;
    const from = b.get(key) ?? 0;
    return {
      label: statLabel(key),
      from,
      to,
      direction: (to > from ? 1 : to < from ? -1 : 0) as -1 | 0 | 1,
    };
  });

  /*
    **Gains first, then losses, then anything unchanged** — grouped rather than alphabetical.

    Asked for directly, and it is the right call for the same reason the arrow exists: a player
    reading a comparison is asking *what do I get and what do I pay*, and an alphabetical list makes
    them answer that themselves by scanning colours down a column. Grouping answers it by position,
    so the shape of the trade is visible before a single word is read.

    Alphabetical **within** each group, so a list is still stable and scannable — the ordering only
    changes between groups, not inside them.
  */
  const rank = (d: Delta) => (d.direction > 0 ? 0 : d.direction < 0 ? 1 : 2);

  return deltas.sort((x, y) => rank(x) - rank(y) || x.label.localeCompare(y.label));
}

/**
 * A hero-metric delta as a percentage of what is worn, or null when there is no baseline.
 *
 * `+42 DPS` is a fact and `+18.2%` is a judgement — a player needs both, because 42 is enormous on a
 * dagger and marginal on a greatsword. Null rather than infinity when the baseline is zero: an empty
 * hand has no percentage, and "+∞%" is not a number anybody can act on.
 */
export function percentOf(delta: number, baseline: number): number | null {
  if (baseline <= 0) return null;
  return (delta / baseline) * 100;
}

/**
 * Whether a candidate is worth a second look at a glance — **from derived numbers, not item level**.
 *
 * ── What this replaced, and why ──────────────────────────────────────────────
 *
 * It used to be `candidate.itemLevel > worn.itemLevel`, and the comment above it admitted the problem
 * in its own words: *totals depend on affixes the simulation does not read yet, so a "better"
 * computed from them would be a confident lie.* That was true when it was written and is not any
 * more. Two numbers now exist that the simulation reads all the way through:
 *
 *   - `dps` — a weapon's band times the rate it actually swings at, both from the server's own
 *     `character.AttacksPerSecond`, so the quoted rate is the swung rate.
 *   - `toughnessDelta` — the change in `combat.EffectiveHealth`, computed server-side by re-deriving
 *     the loadout with this item in its socket.
 *
 * Item level was never wrong about *newer content*; it was wrong about *better*, which is the
 * question the arrow asks. A level-50 base with three useless affixes outranked a level-40 one that
 * doubled your armour.
 *
 * ── Which number decides ─────────────────────────────────────────────────────
 *
 * A weapon is judged on DPS and everything else on toughness, because those are the questions those
 * items answer. A weapon may also carry armour and a chest piece may carry damage, but leading with
 * the wrong one would rank a greataxe by how much armour it happens to have.
 *
 * **Falls back to nothing, not to item level.** An item the server sent no numbers for gets no arrow
 * rather than a guess — the rule the tooltip follows for a missing requirement. Nothing worn is still
 * an upgrade: an empty socket is strictly worse than a filled one.
 */
export function isUpgrade(candidate: InventoryItem, worn: InventoryItem | undefined): boolean {
  if (!worn) return true;

  // A weapon: compare what they do per second. Both sides carry the number, so the client subtracts
  // rather than asking the server for a second delta.
  if ((candidate.dps ?? 0) > 0 || (worn.dps ?? 0) > 0) {
    return (candidate.dps ?? 0) > (worn.dps ?? 0);
  }

  return (candidate.toughnessDelta ?? 0) > 0;
}

/** How much more damage per second a candidate does than what is worn, or null when neither is a weapon. */
export function dpsDelta(
  candidate: InventoryItem,
  worn: InventoryItem | undefined,
): number | null {
  const mine = candidate.dps ?? 0;
  const theirs = worn?.dps ?? 0;
  if (mine <= 0 && theirs <= 0) return null;
  return mine - theirs;
}

/**
 * Human wording for a stat key — **the one table, covering the whole vocabulary**.
 *
 * ── Why one, and why it is walked ───────────────────────────────────────────
 *
 * There were two: this and a shorter one in `passives.ts`, each covering the keys its own
 * screen happened to show. Both fell back to the raw key with underscores stripped, so a
 * stat neither listed rendered as `crit damage pct` on an item tooltip — reported directly:
 * *some attribute descriptions are named by their id instead of a human friendly readable
 * attribute*.
 *
 * The fallback is the fault, not the fix. A missing entry is invisible in review and looks
 * like data corruption to a player, so `internal/config/statlabels_test.go` walks
 * `config.StatKeys` — the closed vocabulary the YAML is validated against — and fails if any
 * key is missing a label here. The same shape as the `CLOSE_DISPLACED` check: a vocabulary
 * written twice is held against the other copy, never against itself.
 *
 * Percentages carry their `%` in the wording rather than in the value, because that is where
 * a reader expects it: *Critical chance 5%* reads as a stat and *Critical chance % 5* does
 * not.
 */
const STAT_WORDS: Record<string, string> = {
  // Offence.
  physical_damage_flat: 'Physical damage',
  physical_damage_pct: 'Physical damage',
  damage_min: 'Minimum damage',
  damage_max: 'Maximum damage',
  attack_speed: 'Attack speed',
  attack_range: 'Attack range',
  attack_speed_pct: 'Attack speed',
  cast_speed_pct: 'Cast speed',
  crit_chance_pct: 'Critical chance',
  crit_damage_pct: 'Critical damage',
  spell_crit_chance_pct: 'Spell critical chance',
  spell_damage_pct: 'Spell damage',
  // Defence.
  armor: 'Armour',
  // `armour` is the client's own key for an item's *base* armour — the flat number on the
  // base type rather than an affix — so it is not in `config.StatKeys` and the walk does not
  // demand it. Spelt out anyway: relying on the fallback to produce the same word is luck.
  armour: 'Armour',
  armor_flat: 'Armour',
  // The increased-% pool. Worded as the bare noun like every other `_pct` key here —
  // `physical_damage_pct` is 'Physical damage' — because a comparison row renders the
  // sign and the unit itself, and 'Increased armour +20%' reads as a stat about
  // increases. Go's `item.StatLabel` says "Increased Armour" instead, because there it
  // is a finished affix line on a tooltip with no separate unit column.
  armor_pct: 'Armour',
  evasion: 'Evasion',
  block_chance_pct: 'Block chance',
  health_flat: 'Maximum health',
  health_pct: 'Maximum health',
  health_regen_flat: 'Health regeneration',
  mana_flat: 'Maximum mana',
  mana_pct: 'Maximum mana',
  mana_regen_flat: 'Mana regeneration',
  mana_regen_pct: 'Mana regeneration',
  fire_res_pct: 'Fire resistance',
  cold_res_pct: 'Cold resistance',
  lightning_res_pct: 'Lightning resistance',
  all_res_pct: 'All resistances',
  // Attributes and utility.
  strength: 'Strength',
  dexterity: 'Dexterity',
  intelligence: 'Intelligence',
  endurance: 'Endurance',
  all_attributes: 'All attributes',
  move_speed_pct: 'Movement speed',
  potion_efficiency_pct: 'Potion potency',
  potion_capacity: 'Potion charges',
};

/**
 * One stat key as a **noun**, with no unit — *Physical damage*, *Critical chance*.
 *
 * The unit is the caller's, because the two screens that name stats put it in different
 * places: an item tooltip writes the value first (`+12% Physical damage`) and the passives
 * panel writes it per rank (`+3% Physical damage per rank`). A `%` baked into the noun would
 * be doubled by one of them — which is exactly what happened when there were two tables.
 *
 * The fallback capitalises and de-underscores rather than showing the raw key, so a stat from
 * a newer server degrades to *Crit damage pct* rather than `crit_damage_pct`. It is still a
 * fallback nobody should reach — see the walk above.
 */
export function statNoun(key: string): string {
  const known = STAT_WORDS[key];
  if (known) return known;
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** True for stat keys whose value is a percentage rather than a flat amount. */
export function isPercentageStat(key: string): boolean {
  return key.endsWith('_pct');
}

/** A stat key in words, carrying its unit — the wording a comparison column wants. */
export function statLabel(key: string): string {
  return isPercentageStat(key) ? `${statNoun(key)} %` : statNoun(key);
}

/** Every stat this client can name, for the cross-language check. */
export const KNOWN_STAT_KEYS = Object.keys(STAT_WORDS);

/**
 * An affix rendered for a tooltip, using the server's own wording where present.
 *
 * The unit goes on the **number** here — `+18% Physical damage` — rather than on the noun,
 * which is where `statLabel` puts it. Both are right for their own column: an affix line
 * reads as a sentence, and a comparison row reads as a table where the value sits in a
 * different column from the name and *Physical damage % 0 → 18* is the only wording that
 * says what the two numbers are.
 */
export function affixLine(affix: ItemAffix): string {
  if (affix.display) return affix.display;
  const sign = affix.value < 0 ? '−' : '+';
  const unit = isPercentageStat(affix.stat) ? '%' : '';
  return `${sign}${Math.abs(affix.value)}${unit} ${statNoun(affix.stat)}`;
}

/**
 * Whether an affix helps or hurts: `1` for a buff, `-1` for a debuff, `0` for neither.
 *
 * The sign of the value and nothing cleverer. Every stat in the vocabulary is one a player
 * wants *more* of — there is no `damage_taken_pct` — so a negative roll is a downside on a
 * mixed affix and a positive one is not, and reading it any other way would need a table of
 * polarity per stat that would silently be wrong the first time a stat was added to it.
 *
 * Asked for directly: *the buffs and debuffs displayed should be in colour, red when a
 * debuff, green when a buff*. Colour is the only signal that survives a glance at a tooltip
 * with eight lines on it, which is why a leading `−` is not enough on its own.
 *
 * A zero-valued affix is neither, and it is drawn in the ordinary text colour rather than
 * being hidden: an affix that rolled to nothing is a fact about the item.
 */
export function affixSign(affix: ItemAffix): -1 | 0 | 1 {
  if (affix.value > 0) return 1;
  if (affix.value < 0) return -1;
  return 0;
}
